// 設備表格（DEV-01）的純函式：展平、篩選、排序、虛擬捲動的可見窗。
import { macMatches, type Presence, type VmsDevice } from '../../lib/devices';

export interface DevRow {
  d: VmsDevice;
  depth: number;
  /** 通道 / 子設備的 parent（列名寫「NVR631 › CH1」） */
  parent?: VmsDevice;
  /** 篩選時 parent 本身不符合、只因底下有符合的通道才出現：灰色、只當分組標題（不算進數字；SPEC DEV-01） */
  dim?: boolean;
  /** 樹狀檢視的群組標題（場域 / 家戶）；其他列沒有 */
  group?: { key: string; label: string; count: number };
}

export type SortKey = 'name' | 'home' | 'model' | 'firmware' | 'status' | 'last_seen';
export type PresenceFilter = 'all' | Presence;

const PRES_RANK: Record<Presence, number> = { offline: 0, unknown: 1, online: 2 };

function cmp(a: VmsDevice, b: VmsDevice, key: SortKey): number {
  switch (key) {
    case 'status':
      return PRES_RANK[a.presence] - PRES_RANK[b.presence];
    case 'last_seen':
      return (Date.parse(a.lastSeen) || 0) - (Date.parse(b.lastSeen) || 0);
    case 'home':
      return a.homeName.localeCompare(b.homeName);
    case 'model':
      return a.model.localeCompare(b.model);
    case 'firmware':
      return a.firmware.localeCompare(b.firmware, undefined, { numeric: true });
    default:
      return a.name.localeCompare(b.name, undefined, { numeric: true });
  }
}

export function sortDevices(list: VmsDevice[], key: SortKey, dir: 1 | -1): VmsDevice[] {
  return [...list].sort((a, b) => cmp(a, b, key) * dir || a.name.localeCompare(b.name, undefined, { numeric: true }));
}

/** 狀態篩選：頂層符合、或它底下有通道符合（通道看通道自己的狀態）。 */
export function presenceMatches(d: VmsDevice, f: PresenceFilter): boolean {
  return f === 'all' || d.presence === f || d.children.some((c) => c.presence === f);
}

/** 設備自己（不看通道）符不符合搜尋字。 */
export function selfMatches(d: Pick<VmsDevice, 'name' | 'model' | 'aid' | 'homeName' | 'macSuffix'>, q: string): boolean {
  const n = q.trim().toLowerCase();
  return !n || macMatches(d.macSuffix, n) || [d.name, d.model, d.aid, d.homeName].some((v) => v.toLowerCase().includes(n));
}

/**
 * 篩選後的列與數字（SPEC DEV-01）：條件 ok(d, parent) 看設備自己（狀態 / 類型 / 憑證；通道可看 parent，例「錄影主機」的通道跟著主機）；搜尋字對 parent 符合時底下的通道都算。
 * 回：要列的頂層（自己符合或有符合的通道）、列用的 match、數字（設備不含通道；離線通道只算 parent 在線的，跟總覽一樣）。
 */
export function filterTree(
  top: VmsDevice[],
  ok: (d: VmsDevice, parent?: VmsDevice) => boolean,
  q: string,
  pres: PresenceFilter,
): { shown: VmsDevice[]; match: (d: VmsDevice, parent?: VmsDevice) => boolean; devices: number; channels: number } {
  const match = (d: VmsDevice, parent?: VmsDevice) => ok(d, parent) && (selfMatches(d, q) || (!!parent && selfMatches(parent, q)));
  const shown: VmsDevice[] = [];
  let devices = 0;
  let channels = 0;
  for (const d of top) {
    const self = match(d);
    const kids = d.children.filter((c) => match(c, d));
    if (!self && !kids.length) continue;
    shown.push(d);
    if (self) devices++;
    channels += pres === 'offline' ? kids.filter(() => d.presence === 'online').length : kids.length;
  }
  return { shown, match, devices, channels };
}

/** 網址的類型篩選（`/devices?type=camera|recorder|sensor`，SPEC DEV-01）。 */
export type TypeFilter = '' | 'camera' | 'recorder' | 'sensor';

/** 設備表格的選用欄位（「欄位 ▾」，預設不顯示；SPEC DEV-01）。 */
export type OptCol = 'home' | 'recording' | 'ai' | 'location' | 'reason';
export const OPT_COLS: OptCol[] = ['home', 'recording', 'ai', 'location', 'reason'];
const COLS_KEY = 'kinio.vms.devcols';

/** 選用欄位存在這個瀏覽器（每人的偏好；讀不到 = 都不顯示）。 */
export function readOptCols(): Set<OptCol> {
  try {
    const v = JSON.parse(localStorage.getItem(COLS_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(v) ? (v.filter((x) => OPT_COLS.includes(x as OptCol)) as OptCol[]) : []);
  } catch {
    return new Set();
  }
}

export function writeOptCols(s: Set<OptCol>): void {
  try {
    localStorage.setItem(COLS_KEY, JSON.stringify([...s]));
  } catch {
    /* 無 storage */
  }
}

/**
 * 表格檢視：頂層設備排序，通道 / 子設備跟在 parent 後面（依通道序），收合的不展開。
 * 樹狀檢視（group）：依 groupOf（場域 / 家戶）分組，組內同表格。
 */
export function flattenRows(
  top: VmsDevice[],
  opts: {
    sort: SortKey;
    dir: 1 | -1;
    collapsed: Set<string>;
    groupOf?: (d: VmsDevice) => { key: string; label: string; order?: number };
    /** 篩選條件（狀態 / 類型）：給了就只列符合的通道，parent 不符合時標 dim */
    match?: (d: VmsDevice, parent?: VmsDevice) => boolean;
  },
): DevRow[] {
  const out: DevRow[] = [];
  const push = (list: VmsDevice[]) => {
    for (const d of sortDevices(list, opts.sort, opts.dir)) {
      const kids = opts.match ? d.children.filter((c) => opts.match!(c, d)) : d.children;
      out.push(opts.match && !opts.match(d) ? { d, depth: 0, dim: true } : { d, depth: 0 });
      if (!opts.collapsed.has(d.key)) for (const c of kids) out.push({ d: c, depth: 1, parent: d });
    }
  };
  if (!opts.groupOf) {
    push(top);
    return out;
  }
  const groups = new Map<string, { label: string; order: number; list: VmsDevice[] }>();
  for (const d of top) {
    const g = opts.groupOf(d);
    const cur = groups.get(g.key) ?? { label: g.label, order: g.order ?? 0, list: [] };
    cur.list.push(d);
    groups.set(g.key, cur);
  }
  // 有 order（「依來源」：我自己的 → 組織授權給我 → 別人分享給我）先照 order，再照名稱
  for (const [key, g] of [...groups.entries()].sort((a, b) => a[1].order - b[1].order || a[1].label.localeCompare(b[1].label))) {
    out.push({ d: g.list[0], depth: -1, group: { key, label: g.label, count: g.list.length } });
    if (!opts.collapsed.has('g:' + key)) push(g.list);
  }
  return out;
}

/** 虛擬捲動：固定列高，回可見範圍（前後多留 overscan 列）。 */
export function visibleWindow(total: number, scrollTop: number, viewport: number, rowH: number, overscan = 10): { start: number; end: number } {
  const start = Math.max(0, Math.floor(scrollTop / rowH) - overscan);
  const end = Math.min(total, Math.ceil((scrollTop + viewport) / rowH) + overscan);
  return { start, end };
}
